import { TRANSACTION_MAP } from "@constants/transaction"
import { client } from "@lib/api"
import { extractData, getDisbursementSummary } from "@lib/utils"
import { RedeemTransactionDetail } from "../types/redemption"
import useSWR from "swr"
import * as Sentry from "@sentry/react"

type Params = {
  uuid?: string
  transaction_uuid?: string
  code: number
  fetchImmediately?: boolean
}

const url = '/v2/wallet/activity/'
const legacyUrl = '/wallet/transactions/'

const useActivityDetail = ({
  uuid,
  transaction_uuid,
  code,
  fetchImmediately = false
}: Params) => {
  const isLegacyTransaction = transaction_uuid !== undefined

  // Create the key that would be used for fetching
  const fetchKey = isLegacyTransaction ? `${legacyUrl}${transaction_uuid}` : `${url}${uuid}`

  // Define the fetcher function outside of useSWR for reuse
  const fetcher = async (url: string) => {
    try {
      const response = await client.doGet(url)
      const isMultipartDisbursement = response.is_multipart
      const activityDetail = extractData(response, isLegacyTransaction ? 'old' : 'new', code, TRANSACTION_MAP)
      if (isMultipartDisbursement) {
        activityDetail.additionalDetail = getDisbursementSummary(
          response as RedeemTransactionDetail,
        )
      }
      return activityDetail
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

  // Use SWR with conditional fetching
  const { data, isLoading, error, mutate } = useSWR(
    fetchImmediately ? fetchKey : null,
    fetcher
  )

  const refetch = async () => {
    try {
      // Manually fetch the data
      const newData = await fetcher(fetchKey)

      // Update the cache with the new data
      await mutate(fetchKey)

      return newData
    } catch (error) {
      return null
    }
  }

  return {
    data: data,
    isLoading,
    error,
    refetch
  }
}

export default useActivityDetail